/**
 * Colours for departments, keyed by name rather than by position.
 *
 * The palette was previously inlined in useEventStats and assigned by array position, so
 * "Logistics" could be teal one moment and pink the next as the list around it changed.
 * Assigning by a hash of the name fixes that.
 *
 * What this does NOT promise is that two screens always agree. departmentColorMap resolves
 * hash collisions by probing over the set it is handed, and different screens hand it
 * different sets -- the pie chart passes departments that have volunteers, the chat
 * carousel passes departments that have chat rows. Two departments whose hashes collide
 * can therefore land differently on each. The common case, where nothing collides, does
 * agree; treat cross-screen consistency as the usual outcome, not a guarantee.
 */

/** The twelve colours the volunteers pie chart has always used. */
export const DEPARTMENT_PALETTE = [
  '#FF6B6B',
  '#FFD166',
  '#4ECDC4',
  '#A78BFA',
  '#F78C6B',
  '#6EE7B7',
  '#F472B6',
  '#60A5FA',
  '#FFA726',
  '#AB47BC',
  '#26A69A',
  '#EF5350',
] as const;

/** FNV-1a over the normalized name. Any stable hash would do; this one is short. */
function hashDepartment(name: string): number {
  let h = 0x811c9dc5;
  const key = name.trim().toLowerCase();
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return Math.abs(h);
}

/** The colour for one department, when the surrounding set is unknown. */
export function departmentColor(name: string): string {
  return DEPARTMENT_PALETTE[hashDepartment(name) % DEPARTMENT_PALETTE.length];
}

/**
 * Colours for a known set of departments, keyed by the normalized name.
 *
 * Each name takes its hashed slot; if that slot is already taken the next free one is
 * used, walking upward. Within one screen that guarantees no two departments share a
 * colour, up to the palette size.
 *
 * The trade-off is the probe: a department that had to move because something else already
 * held its slot lands somewhere that depends on which other departments were in the set.
 * Pass the same set on two screens and they agree; pass different sets and a *colliding*
 * name can differ. Non-colliding names -- the overwhelming majority, with 12 slots and a
 * handful of departments -- are identical either way.
 *
 * Resolved in sorted order so the outcome does not depend on input order.
 */
export function departmentColorMap(names: string[]): Record<string, string> {
  const unique = [...new Set(names.map((n) => n.trim().toLowerCase()).filter(Boolean))].sort();
  const taken = new Set<number>();
  const map: Record<string, string> = {};

  for (const key of unique) {
    let slot = hashDepartment(key) % DEPARTMENT_PALETTE.length;
    // Past the palette size every slot is taken, so stop walking and allow the repeat.
    for (let step = 0; step < DEPARTMENT_PALETTE.length && taken.has(slot); step += 1) {
      slot = (slot + 1) % DEPARTMENT_PALETTE.length;
    }
    taken.add(slot);
    map[key] = DEPARTMENT_PALETTE[slot];
  }
  return map;
}

/** Look a department up in a map from departmentColorMap, falling back to its own hash. */
export function departmentColorFrom(map: Record<string, string>, name: string): string {
  return map[name.trim().toLowerCase()] ?? departmentColor(name);
}

/**
 * Tile colours for a letter avatar: a soft wash of the department's colour with a
 * darkened version of it for the letter, so the pairing always has contrast.
 */
export function departmentTileColors(color: string): { bg: string; fg: string } {
  return { bg: `${color}24`, fg: darken(color, 0.45) };
}

/** Mix a hex colour toward black. Avoids shipping a colour library for one operation. */
function darken(hex: string, amount: number): string {
  const value = hex.replace('#', '');
  const num = parseInt(value, 16);
  const r = Math.round(((num >> 16) & 0xff) * (1 - amount));
  const g = Math.round(((num >> 8) & 0xff) * (1 - amount));
  const b = Math.round((num & 0xff) * (1 - amount));
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}
